import React, { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { fetchOpenRouterModels, safeFetch, type OpenRouterModel } from "../api/client";
import { listBundles, duplicateBundle, updateBundle, type BundleSummary } from "../api/agentBundles";
import { createBundleV2, getExecutionSettings, getWorkbenchMeta, type ExecutionSettings } from "../api/workbench";
import { fmtDate } from "../utils";
import type { CorpusSet, WorkstreamLite } from "../types";
import { BundleView } from "./BundleView";
import type { WorkbenchMeta } from "./types";

/* ===================================================================
   Agent Registry workbench — the Library tab's entry point. Directory
   of bundles (the tables are the source of truth) → Bundle view →
   Agent Builder. Replaces the old flat draft editor.
=================================================================== */

export interface WorkbenchProps {
  corpuses: CorpusSet[];
  workstreams: WorkstreamLite[];
  /** Lets the Library page swap its chrome while a bundle is open. */
  onBundleOpenChange?: (open: boolean) => void;
}

interface DirRow extends BundleSummary {
  versionCount?: number;
  draftDirty?: boolean;
}

export function Workbench(p: WorkbenchProps): JSX.Element {
  const [meta, setMeta] = useState<WorkbenchMeta | null>(null);
  const [settings, setSettings] = useState<ExecutionSettings | null>(null);
  const [models, setModels] = useState<OpenRouterModel[]>([]);
  const [rows, setRows] = useState<DirRow[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDel, setConfirmDel] = useState<DirRow | null>(null);

  const refresh = async (): Promise<void> => {
    setRows((await listBundles()) as DirRow[]);
  };
  useEffect(() => {
    void refresh();
    void getWorkbenchMeta().then(setMeta);
    void getExecutionSettings().then(setSettings);
    void fetchOpenRouterModels().then((m) => {
      if (m) setModels(m);
    });
  }, []);
  const { onBundleOpenChange } = p;
  useEffect(() => {
    onBundleOpenChange?.(openId != null);
    return () => onBundleOpenChange?.(false);
  }, [openId, onBundleOpenChange]);

  if (openId && meta && settings) {
    return (
      <BundleView
        bundleId={openId}
        meta={meta}
        models={models}
        settings={settings}
        corpuses={p.corpuses}
        workstreams={p.workstreams}
        onBack={() => {
          setOpenId(null);
          void refresh();
        }}
        onDeleted={() => {
          setOpenId(null);
          void refresh();
        }}
      />
    );
  }

  const visible = (rows ?? []).filter((r) => (showArchived ? true : !r.isArchived));

  return (
    <div>
      <div className="lbar">
        <span className="up">
          {visible.length} bundle{visible.length === 1 ? "" : "s"}
          <button className="lnk" style={{ marginLeft: 10, fontSize: 11.5, textTransform: "none", letterSpacing: 0 }} onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "hide archived" : "show archived"}
          </button>
        </span>
        <button
          className="btn btn-ghost btn-sm"
          onClick={async () => {
            const name = window.prompt("New bundle name:", "Untitled bundle");
            if (!name) return;
            const b = await createBundleV2({ name, seedSpine: true });
            await refresh();
            if (b) setOpenId(b.id);
          }}
          title="Starts with the minimal spine: cleaner + router + participant resolution"
        >
          <Plus size={14} /> New bundle
        </button>
      </div>
      {rows === null && (
        <div className="tert" style={{ padding: 16 }}>
          <Loader2 size={14} className="spinrot" /> Loading bundles…
        </div>
      )}
      {rows !== null && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr className="up" style={{ fontSize: 10.5, textAlign: "left", color: "var(--tert)" }}>
                  <th style={{ padding: "10px 14px" }}>Bundle</th>
                  <th style={{ padding: "10px 8px" }}>Agents</th>
                  <th style={{ padding: "10px 8px" }}>Versions</th>
                  <th style={{ padding: "10px 8px" }}>Latest</th>
                  <th style={{ padding: "10px 8px" }}>Saved</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--bd)", opacity: r.isArchived ? 0.55 : 1 }}>
                    <td style={{ padding: "10px 14px", cursor: "pointer" }} onClick={() => setOpenId(r.id)}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontWeight: 600 }}>{r.name}</span>
                        {r.draftDirty && (
                          <span className="chip dirty" title="The draft has unsaved changes since the latest version.">
                            draft · unsaved changes
                          </span>
                        )}
                        {r.isArchived && <span className="tchip tert">archived</span>}
                      </div>
                      {r.note && <div className="tert" style={{ fontSize: 11.5 }}>{r.note}</div>}
                    </td>
                    <td style={{ padding: "10px 8px" }} className="mono">{r.agentCount}</td>
                    <td style={{ padding: "10px 8px" }} className="mono">{r.versionCount ?? "—"}</td>
                    <td style={{ padding: "10px 8px" }}>{r.latestVersionLabel ?? <span className="tert">none</span>}</td>
                    <td style={{ padding: "10px 8px" }} className="tert">{r.latestVersionAt ? fmtDate(r.latestVersionAt) : "—"}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(r.id)}>Open</button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--muted)" }}
                          title="Copy the current draft into a new bundle (empty version history)"
                          onClick={async () => {
                            const name = window.prompt("New from this — name for the new bundle:", `${r.name} (copy)`);
                            if (!name) return;
                            await duplicateBundle(r.id, name);
                            await refresh();
                          }}
                        >
                          New from this
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--muted)" }}
                          title={r.isArchived ? "Unarchive" : "Archive — hidden from pickers, versions stay runnable"}
                          onClick={async () => {
                            await updateBundle(r.id, { isArchived: !r.isArchived });
                            await refresh();
                          }}
                        >
                          {r.isArchived ? "Unarchive" : "Archive"}
                        </button>
                        <button className="btn btn-ghost btn-sm" style={{ color: "var(--bad)" }} title="Delete bundle" onClick={() => setConfirmDel(r)}>
                          Delete
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={6} className="tert" style={{ padding: 18, fontSize: 12.5 }}>
                      No bundles yet — the server seeds the canonical roster on boot; create one with “New bundle”.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirmDel && (
        <>
          <div className="backdrop" style={{ zIndex: 60 }} onClick={() => setConfirmDel(null)} />
          <div className="card" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 61, width: "min(440px, 92vw)", padding: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: "var(--red)" }}>Delete "{confirmDel.name}"?</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, marginBottom: 12 }}>
              This deletes the draft and all {confirmDel.versionCount ?? 0} saved version{(confirmDel.versionCount ?? 0) === 1 ? "" : "s"}. Simulations pinned to its versions will no longer run. Archiving keeps versions runnable; deleting does not. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ color: "var(--red)", borderColor: "var(--red)" }}
                onClick={async () => {
                  await safeFetch(`/agent-bundles/${encodeURIComponent(confirmDel.id)}`, { method: "DELETE" });
                  setConfirmDel(null);
                  await refresh();
                }}
              >
                Delete bundle
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
